import React, { useState, useRef } from 'react';
import './UploadModal.css';
import { API_BASE_URL } from '../../../../API/api';

const UploadModal = ({ isOpen, onClose, onUpload, onFileAdded, projectId, currentFolderId, projectName, currentFolderName }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    // Initialize progress and status for each file
    const progress = {};
    const status = {};
    files.forEach((file, index) => {
      progress[index] = 0;
      status[index] = 'pending';
    });
    setUploadProgress(progress);
    setUploadStatus(status);
  };

  const handleRemoveFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    const newProgress = { ...uploadProgress };
    const newStatus = { ...uploadStatus };
    delete newProgress[index];
    delete newStatus[index];
    // Reindex remaining files
    const reindexedProgress = {};
    const reindexedStatus = {};
    newFiles.forEach((_, i) => {
      reindexedProgress[i] = newProgress[i] || 0;
      reindexedStatus[i] = newStatus[i] || 'pending';
    });
    setUploadProgress(reindexedProgress);
    setUploadStatus(reindexedStatus);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    const results = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadStatus(prev => ({ ...prev, [i]: 'uploading' }));
      setUploadProgress(prev => ({ ...prev, [i]: 0 }));

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('parent_id', currentFolderId || projectId);

        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(prev => ({ ...prev, [i]: percentComplete }));
          }
        });

        const uploadPromise = new Promise((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status === 200 || xhr.status === 201) {
              const response = JSON.parse(xhr.responseText);
              setUploadStatus(prev => ({ ...prev, [i]: 'success' }));
              setUploadProgress(prev => ({ ...prev, [i]: 100 }));
              resolve(response);
            } else {
              const error = JSON.parse(xhr.responseText);
              setUploadStatus(prev => ({ ...prev, [i]: 'error' }));
              reject(new Error(error.error || 'Upload failed'));
            }
          };

          xhr.onerror = () => {
            setUploadStatus(prev => ({ ...prev, [i]: 'error' }));
            reject(new Error('Network error'));
          };

          xhr.open('POST', `${API_BASE_URL}/api/projects/${projectId}/upload`);
          xhr.send(formData);
        });

        const result = await uploadPromise;
        results.push({ file: file.name, success: true, result });
        
        // Add file to current view without full refresh
        if (result.file && onFileAdded) {
          onFileAdded(result.file);
        }

      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        setUploadStatus(prev => ({ ...prev, [i]: 'error' }));
        results.push({ file: file.name, success: false, error: error.message });
      }
    }

    setUploading(false);

    // Show summary
    const successCount = results.filter(r => r.success).length;
    const errorCount = results.filter(r => !r.success).length;

    if (errorCount === 0) {
      if (successCount > 0) {
        // Only show alert if we didn't use the elegant method
        if (!onFileAdded) {
          alert(`Successfully uploaded ${successCount} file(s)`);
          onUpload(); // Fallback to refresh if onFileAdded not available
        }
      }
      handleClose();
    } else {
      alert(`Uploaded ${successCount} file(s), ${errorCount} failed. Check console for details.`);
      if (successCount > 0 && !onFileAdded) {
        onUpload(); // Fallback to refresh if onFileAdded not available
      }
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadStatus({});
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return 'fas fa-check-circle success';
      case 'error':
        return 'fas fa-times-circle error';
      case 'uploading':
        return 'fas fa-spinner fa-spin uploading';
      default:
        return 'fas fa-clock pending';
    }
  };

  return (
    <div className="upload-modal-overlay" onClick={handleClose}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upload-modal-header">
          <h2>Upload Files</h2>
          <button className="upload-modal-close" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="upload-modal-content">
          <div className="upload-info">
            <p>
              <i className="fas fa-folder"></i>
              Uploading to: <strong>{currentFolderName || projectName || 'Project Root'}</strong>
            </p>
          </div>

          <div className="upload-file-selector">
            <input
              type="file"
              ref={fileInputRef}
              multiple
              onChange={handleFileSelect}
              className="upload-file-input"
              id="file-upload-input"
            />
            <label htmlFor="file-upload-input" className="upload-file-label">
              <i className="fas fa-cloud-upload-alt"></i>
              <span>Select Files</span>
            </label>
          </div>

          {selectedFiles.length > 0 && (
            <div className="upload-file-list">
              <h3>Selected Files ({selectedFiles.length})</h3>
              <div className="upload-file-items">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="upload-file-item">
                    <div className="upload-file-info">
                      <i className="fas fa-file"></i>
                      <div className="upload-file-details">
                        <span className="upload-file-name">{file.name}</span>
                        <span className="upload-file-size">{formatFileSize(file.size)}</span>
                      </div>
                    </div>
                    <div className="upload-file-status">
                      {uploadStatus[index] === 'uploading' && (
                        <div className="upload-progress-bar">
                          <div 
                            className="upload-progress-fill" 
                            style={{ width: `${uploadProgress[index] || 0}%` }}
                          ></div>
                        </div>
                      )}
                      <i className={getStatusIcon(uploadStatus[index] || 'pending')}></i>
                      {!uploading && uploadStatus[index] !== 'uploading' && (
                        <button
                          className="upload-remove-btn"
                          onClick={() => handleRemoveFile(index)}
                          title="Remove file"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="upload-modal-footer">
          <button className="upload-cancel-btn" onClick={handleClose} disabled={uploading}>
            Cancel
          </button>
          <button 
            className="upload-submit-btn" 
            onClick={handleUpload} 
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Uploading...
              </>
            ) : (
              <>
                <i className="fas fa-upload"></i>
                Upload {selectedFiles.length > 0 ? `${selectedFiles.length} ` : ''}File{selectedFiles.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;

